// 戴冠獣の予備動作の声 (2026-10-13・音の専用セッション・記録 = docs/research/2026-10-13-boss-sound-identity.md)。
//   ユーザー原文 (10-13)「戴冠獣だったらもっと獣みたいなガオとかグゥみたいなのを予備動作の効果音に使ってほしい」。
//   声は体が立てる音 = 予告音は攻撃の効果音の決まりの内 (露骨な合図の層は足さない)。全ボス共通の唸り (telegraph_growl) の代わりに鳴る。
//   技の重さで 5 段: 鼻息 < 短い唸り (グゥ) < 長い唸り < 吠え (ガオ) < 咆哮。名前が hound_voice_ で始まる = ボスの技の音
//   (src/sound_priority.ts: プレイヤーの弾き・被弾が鳴ると −9dB 下がる)。
//   既定 b = 獅子寄り (10-13 答えるページでユーザーが b・配布版)。開発版 ?houndVoice=a (狼寄り) / off (前の音 = 共通の唸り)。
//   URL は game.ts の DEV の箱の中でだけ読む。素材 = scripts/render_hound_voice_sfx.py → public/sfx/hound_voice_<a|b>_<役>.wav
//   (b は配布版の preloadSfxSamples が読む・a は開発版で選んだ時だけ読む)。
import type { SfxId } from './types'

export const HOUND_VOICE_TAKES = ['a', 'b'] as const
export type HoundVoiceTake = typeof HOUND_VOICE_TAKES[number]
export type HoundVoice = 'off' | HoundVoiceTake
export const HOUND_VOICE_ROLES = ['huff', 'growl_s', 'growl_l', 'bark', 'roar'] as const
export type HoundVoiceRole = typeof HOUND_VOICE_ROLES[number]
export const HOUND_VOICE_SFX_IDS = HOUND_VOICE_TAKES.flatMap(k => HOUND_VOICE_ROLES.map(r => `hound_voice_${k}_${r}` as const))

export const HOUND_VOICE_DEFAULT: HoundVoice = 'b'
let voice: HoundVoice = HOUND_VOICE_DEFAULT
export function getHoundVoice(): HoundVoice { return voice }
export function setHoundVoice(value: string | null | undefined): HoundVoice {
  voice = value === 'a' || value === 'off' ? value : HOUND_VOICE_DEFAULT
  if (voice === 'a' && typeof fetch !== 'undefined') void import('./audio').then(m => m.preloadHoundVoice('a'))
  return voice
}

// 溜めの頭で鳴らす声の段 (技の重さ)。ここに無い技 (致命など) は共通の唸りのまま。
const TELL_ROLE: Readonly<Record<string, HoundVoiceRole>> = {
  houndPassRun: 'huff', houndTripleSwipe2: 'huff', houndTriple2Fang: 'huff',
  houndBite: 'growl_s', houndSwipe: 'growl_s', houndTripleSwipe1: 'growl_s', houndTriple2Claw: 'growl_s',
  houndLunge: 'growl_l', houndTripleSlam: 'growl_l', houndTriple2Slam: 'growl_l', houndTailSweep: 'growl_l', houndRoar: 'growl_l',
  houndPounce: 'bark', houndGrab: 'bark',
}
export function houndVoiceRoleFor(attackId: string): HoundVoiceRole | null { return TELL_ROLE[attackId] ?? null }

/** 溜めに入った時の声。null = 共通の唸りのまま (off・対象外の技)。 */
export function houndTellSfx(attackId: string): SfxId | null {
  const role = houndVoiceRoleFor(attackId)
  return voice === 'off' || !role ? null : `hound_voice_${voice}_${role}` as SfxId
}
/** 当たりに入った時の声 (灰の咆哮だけ・口を開いた瞬間に吠える)。 */
export function houndActiveSfx(attackId: string): SfxId | null {
  return voice === 'off' || attackId !== 'houndRoar' ? null : `hound_voice_${voice}_roar` as SfxId
}

/** 先読みする素材 [キー, URL]。キー = 効果音の名前。 */
export function houndVoiceFiles(take: HoundVoiceTake): [string, string][] {
  return HOUND_VOICE_ROLES.map(r => `hound_voice_${take}_${r}`).map(id => [id, `/sfx/${id}.wav?v=1`])
}
